import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { InMemoryScrollingFeature, provideRouter, withInMemoryScrolling } from '@angular/router';
import { BASE_PATH as BASE_PATH_BE } from '@generated/be';
import { BE_API_URL, provideRezaRestServices } from '@reza-ais-fe/reza-ais-fe-utils';
import { BASE_TITLE_TOKEN } from '@shared/modules/page-settings';
import { FORM_ERROR_PROVIDER, FormFieldUniqNames } from '@shared/ui/forms';
import {
  AdvancedConsoleProvider,
  AppCanActivate,
  AuthGuard,
  AuthService,
  Environment,
  ENVIRONMENT_JSON,
  errorApiInterceptor,
  jwtInterceptor,
  loadingInterceptor,
  LOGGER_PROVIDERS,
  LogLevel,
  MIN_LOG_LEVEL,
  processRequestInterceptor,
  RouteParamsService,
  StorageService,
  UIService,
} from '@shared/utils';
import { MessageService } from '@openng/optimus-ui/api';
import { provideGovPrimeNG } from '@shared/themes';
import { appRoutes } from './app.routes';

const inMemoryScrollingFeature: InMemoryScrollingFeature = withInMemoryScrolling({
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
});

export function createApplicationConfig(envJson: Environment): ApplicationConfig {
  const appConfig: ApplicationConfig = {
    providers: [
      // aplikace běží bez zone.js – změny musí ohlásit signál, async pipe nebo markForCheck
      provideZonelessChangeDetection(),
      provideRouter(appRoutes, inMemoryScrollingFeature),
      provideHttpClient(
        withXhr(),
        // pořadí jako dřívější HTTP_INTERCEPTORS
        withInterceptors([errorApiInterceptor, processRequestInterceptor, jwtInterceptor, loadingInterceptor])
      ),
      // gov.cz téma (Optimus + Aura); výchozí pořadí vrstev `theme, base, optimus, ds` musí souhlasit se styles.css.
      provideGovPrimeNG(),

      { provide: ENVIRONMENT_JSON, useValue: envJson },
      { provide: LOGGER_PROVIDERS, useClass: AdvancedConsoleProvider, multi: true },
      {
        provide: MIN_LOG_LEVEL,
        useValue: isDevMode() ? LogLevel.ERROR : LogLevel.NEVER,
      },
      {
        provide: FORM_ERROR_PROVIDER,
        useValue: {},
      },
      {
        provide: BASE_TITLE_TOKEN,
        useValue: 'REZA AIS',
      },

      /**
       * rest api endpoints
       */
      {
        provide: BASE_PATH_BE,
        useValue: BE_API_URL,
      },
      // generované REST služby BE – jedna instance pro celou aplikaci (routy je nevyjmenovávají)
      provideRezaRestServices(),
      AuthService,
      RouteParamsService,
      MessageService,
      FormFieldUniqNames,
      StorageService,

      AuthGuard,
      AppCanActivate,

      // ✅ kontrola tokenu ještě před načtením routy
      provideAppInitializer(() => {
        inject(UIService); // vytvořit službu už při startu (dříve v deps APP_INITIALIZER)
        inject(AuthService).validateTokenAgeOnStartup(); // synchronní metoda
      }),
    ],
  };
  return appConfig;
}
